// src/components/Games/NumericSlotMachine/NumericSlotMachine.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, InputGroup, FormControl, Card, Spinner, Alert } from 'react-bootstrap';
import {
  FaCoins,
  FaPlay,
  FaRedoAlt,
  FaHistory,
  FaArrowLeft,
  FaInfoCircle,
  FaVolumeUp,
  FaVolumeMute,
  FaTimesCircle,
  FaRegGrinStars,
  FaSadTear,
  FaGamepad,
  FaCheckCircle,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import gameService from '../../../services/gameService';
import './NumericSlotMachine.css';

const MIN_BET = 1;
const MAX_BET = 1_000_000;

const NumericSlotMachine = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // ====== Estado principal
  const [betAmount, setBetAmount] = useState(1);
  const [reels, setReels] = useState([0, 0, 0]);
  const [result, setResult] = useState(null); // 'jackpot' | 'win' | 'lose' | null
  const [pattern, setPattern] = useState('none'); // 'triple'|'pair'|'straight'|'none'
  const [winnings, setWinnings] = useState(0);
  const [balance, setBalance] = useState(null);

  // UI flags
  const [loading, setLoading] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [error, setError] = useState(null);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [gameUnavailable, setGameUnavailable] = useState(false); // ⛔ juego inactivo

  // Refs: audio y control de giro
  const spinAudioRef = useRef(null);
  const winAudioRef = useRef(null);
  const clickAudioRef = useRef(null);
  const spinIntervalRef = useRef(null);
  const activeSpinTokenRef = useRef(null); // evita condiciones de carrera

  // ====== Helpers de audio
  const safePlay = (audioRef) => {
    try {
      if (audioRef?.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(() => {});
      }
    } catch {}
  };
  const safeStop = (audioRef) => {
    try {
      if (audioRef?.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    } catch {}
  };

  // ====== UX: click con sonido
  const handleButtonClick = (action) => {
    if (!isMuted) safePlay(clickAudioRef);
    action();
  };

  // ====== Validar/corregir apuesta
  const onChangeBet = (val) => {
    const n = Number(val);
    if (!Number.isFinite(n)) {
      setBetAmount(MIN_BET);
      return;
    }
    // Forzar entero
    const intVal = Math.trunc(n);
    const clamped = Math.min(MAX_BET, Math.max(MIN_BET, intVal));
    setBetAmount(clamped);
  };

  const betError = useMemo(() => {
    if (!Number.isInteger(betAmount)) return t('numericSlot.bet_integer_error');
    if (betAmount < MIN_BET || betAmount > MAX_BET)
      return t('numericSlot.bet_range_error', { min: MIN_BET, max: MAX_BET });
    return null;
  }, [betAmount, t]);

  // ====== Animación de carretes (visual) mientras llega backend
  const simulateReels = () =>
    new Promise((resolve) => {
      let ticks = 0;
      const TOTAL = 11;
      clearInterval(spinIntervalRef.current);
      spinIntervalRef.current = setInterval(() => {
        setReels([
          Math.floor(Math.random() * 10),
          Math.floor(Math.random() * 10),
          Math.floor(Math.random() * 10),
        ]);
        ticks += 1;
        if (ticks >= TOTAL) {
          clearInterval(spinIntervalRef.current);
          resolve();
        }
      }, 90);
    });

  // ====== Inicio de giro
  const handlePlay = async () => {
    if (betError) {
      setError(betError);
      return;
    }
    if (loading) return;

    setLoading(true);
    setError(null);
    setShowResultModal(false);
    setGameUnavailable(false); // reset flag al intentar

    // token para ignorar respuestas viejas si el usuario llega a disparar algo
    const token = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    activeSpinTokenRef.current = token;

    try {
      if (!isMuted) safePlay(spinAudioRef);
      await simulateReels();

      // Llamada al backend
      const data = await gameService.playNumericSlotMachine(betAmount);
      // Si llega una respuesta vieja, la ignoramos
      if (activeSpinTokenRef.current !== token) return;

      // Asegurar campos
      const serverReels = Array.isArray(data.reels) ? data.reels : reels;
      const serverResult = data.result ?? 'lose';
      const serverPattern = data.pattern ?? 'none';
      const serverWinnings = typeof data.winnings === 'number' ? data.winnings : 0;
      const serverBalance =
        typeof data.newBalance === 'number'
          ? data.newBalance
          : typeof data.balance === 'number'
          ? data.balance
          : balance;

      setReels(serverReels);
      setResult(serverResult);
      setPattern(serverPattern);
      setWinnings(serverWinnings);
      setBalance(serverBalance);

      if (!isMuted && serverResult !== 'lose') safePlay(winAudioRef);

      setShowResultModal(true);
    } catch (e) {
      const backendMsg =
        e?.response?.data?.error ||
        e?.message ||
        t('numericSlot.generic_error');

      // 🔒 Si el juego está inactivo, deshabilitamos “Jugar”
      if (
        e?.response?.status === 403 &&
        (backendMsg === 'This game is currently unavailable' ||
          backendMsg === 'Game not found' ||
          backendMsg === 'Invalid game ID')
      ) {
        setGameUnavailable(true);
        setError(t('numericSlot.game_unavailable'));
      } else {
        setError(backendMsg);
      }
    } finally {
      setLoading(false);
      safeStop(spinAudioRef);
      clearInterval(spinIntervalRef.current);
    }
  };

  // ====== Reset
  const handleRestart = () => {
    setReels([0, 0, 0]);
    setResult(null);
    setPattern('none');
    setWinnings(0);
    setShowResultModal(false);
    setError(null);
  };

  // ====== Historial
  const fetchHistory = async () => {
    try {
      const data = await gameService.getNumericSlotMachineHistory();
      setHistory(Array.isArray(data) ? data : []);
      setShowHistoryModal(true);
    } catch (e) {
      const backendMsg = e?.response?.data?.error || t('numericSlot.generic_error');
      // También marcar inactivo si viene 403
      if (
        e?.response?.status === 403 &&
        (backendMsg === 'This game is currently unavailable' ||
          backendMsg === 'Game not found' ||
          backendMsg === 'Invalid game ID')
      ) {
        setGameUnavailable(true);
        setError(t('numericSlot.game_unavailable'));
      } else {
        setError(backendMsg);
      }
    }
  };

  // ====== Reglas
  const openRulesModal = () => setShowRulesModal(true);
  const closeRulesModal = () => setShowRulesModal(false);

  // ====== Audio toggle
  const toggleAudio = () => setIsMuted((m) => !m);

  // ====== Limpieza al desmontar
  useEffect(() => {
    return () => {
      clearInterval(spinIntervalRef.current);
      safeStop(spinAudioRef);
      safeStop(winAudioRef);
      safeStop(clickAudioRef);
      activeSpinTokenRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ====== Tecla Enter para jugar
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Enter' && !loading && !betError && !gameUnavailable) handlePlay();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, betError, betAmount, gameUnavailable]);

  // ====== Lógica de resaltado (UI)
  const numberIsHighlighted = (index) => {
    if (!result) return false;
    if (pattern === 'straight') return true; // resaltamos todos en escalera
    if (pattern === 'triple') return true;   // todos iguales
    if (pattern === 'pair') {
      const n = reels[index];
      const count = reels.filter((x) => x === n).length;
      return count >= 2;
    }
    return false;
  };

  // Labels localizados (evita objetos estáticos)
  const patternLabel = t(`numericSlot.pattern.${pattern}`, { defaultValue: '—' });
  const resultTitle = result
    ? t(`numericSlot.result_modal_title.${result}`, { defaultValue: t('numericSlot.result_modal_title.win') })
    : '';

  return (
    <div className="slot-numeric-machine-container">
      {/* Audio */}
      <audio ref={spinAudioRef} src="/audio/number-slot.mp3" preload="auto" />
      <audio ref={winAudioRef} src="/audio/win.wav" preload="auto" />
      <audio ref={clickAudioRef} src="/audio/click.mp3" preload="auto" />

      <Card className="text-center slot-numeric-machine-card shadow-lg" role="region" aria-label={t('numericSlot.title')}>
        <Card.Body>
          {/* Header */}
          <div className="position-relative mb-3">
            <Button
              variant="secondary"
              onClick={() => handleButtonClick(() => navigate('/sessions'))}
              className="slot-numeric-back-button"
              title={t('numericSlot.back')}
            >
              <FaArrowLeft className="me-2" />
            </Button>
            <h1 className="slot-numeric-machine-title">{t('numericSlot.title')}</h1>
            <div className="slot-numeric-top-right-buttons">
              <Button variant="link" onClick={() => handleButtonClick(openRulesModal)} title={t('numericSlot.rules')}>
                <FaInfoCircle size={24} />
              </Button>
              <Button
                variant="link"
                onClick={() => handleButtonClick(toggleAudio)}
                title={isMuted ? t('numericSlot.play') : t('numericSlot.rules')}
                aria-label={isMuted ? 'Unmute' : 'Mute'}
              >
                {isMuted ? <FaVolumeMute size={24} /> : <FaVolumeUp size={24} />}
              </Button>
            </div>
          </div>

          {/* Aviso de juego inactivo */}
          {gameUnavailable && (
            <Alert variant="warning" className="mb-3">
              {t('numericSlot.game_unavailable')}
            </Alert>
          )}

          {/* Apuesta */}
          <InputGroup className="mb-2 slot-numeric-bet-input-group" aria-label={t('numericSlot.bet')}>
            <InputGroup.Text>
              <FaCoins />
            </InputGroup.Text>
            <FormControl
              type="number"
              inputMode="numeric"
              min={MIN_BET}
              max={MAX_BET}
              step={1}
              value={betAmount}
              onChange={(e) => onChangeBet(e.target.value)}
              required
              aria-invalid={!!betError}
              aria-describedby="bet-help"
            />
            <InputGroup.Text>{t('numericSlot.currency')}</InputGroup.Text>
          </InputGroup>
          <div id="bet-help" className="form-text text-muted">
            {t('numericSlot.bet_help', { min: MIN_BET, max: MAX_BET })}
          </div>
          {betError && <div className="text-danger mt-1">{betError}</div>}

          {/* Carretes */}
          <div className="slot-numeric-reels-container" aria-live="polite">
            {reels.map((number, index) => (
              <div key={index} className="slot-numeric-reel" aria-label={`Reel ${index + 1}`}>
                <div
                  className={[
                    'slot-numeric-number',
                    loading ? 'spinning' : '',
                    numberIsHighlighted(index) ? 'slot-numeric-winning-number' : '',
                  ].join(' ')}
                >
                  {number}
                </div>
              </div>
            ))}
          </div>

          {/* Acciones */}
          <div className="slot-numeric-actions-container">
            <Button
              onClick={() => handleButtonClick(handlePlay)}
              disabled={loading || !!betError || gameUnavailable}
              className="slot-numeric-machine-button"
            >
              {loading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <>
                  <FaPlay className="me-2" />
                  {t('numericSlot.play')}
                </>
              )}
            </Button>
            <Button
              onClick={() => handleButtonClick(handleRestart)}
              disabled={loading}
              className="slot-numeric-machine-button"
              variant="outline-light"
            >
              <FaRedoAlt className="me-2" /> {t('numericSlot.restart')}
            </Button>
            <Button
              onClick={() => handleButtonClick(fetchHistory)}
              className="slot-numeric-machine-button"
              variant="outline-info"
              disabled={loading}
            >
              <FaHistory className="me-2" /> {t('numericSlot.history')}
            </Button>
          </div>

          {error && (
            <p className="text-danger mt-3" role="alert">
              {error}
            </p>
          )}

          {/* Modal: Resultado */}
          {showResultModal && (
            <div className="slot-numeric-custom-modal" role="dialog" aria-modal="true" aria-label={t('numericSlot.title')}>
              <div className="slot-numeric-modal-content">
                <div className="slot-numeric-modal-header">
                  <h2 className="d-flex align-items-center gap-2">
                    {result !== 'lose' ? (
                      <>
                        <FaRegGrinStars className="me-1" />
                        {resultTitle}
                      </>
                    ) : (
                      <>
                        <FaSadTear className="me-1" />
                        {resultTitle}
                      </>
                    )}
                  </h2>
                  <button className="slot-numeric-modal-close" onClick={() => setShowResultModal(false)} aria-label={t('numericSlot.close')}>
                    <FaTimesCircle />
                  </button>
                </div>
                <div className="slot-numeric-modal-body">
                  <div className="mb-2">
                    <span className="badge bg-warning text-dark me-2">{patternLabel}</span>
                    {typeof winnings === 'number' && (
                      <span className="badge bg-success">
                        {t('numericSlot.winnings_label', { amount: winnings })}
                      </span>
                    )}
                  </div>

                  <h3 className="slot-numeric-result">
                    {result && t(`numericSlot.result_message.${result}`)}
                  </h3>

                  <p className="slot-numeric-balance">
                    {t('numericSlot.new_balance', { amount: balance ?? '—' })}
                  </p>
                  <div className="d-flex gap-2 justify-content-center">
                    <Button onClick={() => handleButtonClick(() => setShowResultModal(false))} className="slot-numeric-modal-button">
                      {t('numericSlot.close')}
                    </Button>
                    <Button
                      onClick={() => handleButtonClick(handlePlay)}
                      disabled={loading || gameUnavailable}
                      className="slot-numeric-modal-button"
                    >
                      <FaPlay className="me-2" /> {t('numericSlot.play_again')}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Modal: Reglas */}
          {showRulesModal && (
            <div className="slot-numeric-custom-modal" role="dialog" aria-modal="true" aria-label={t('numericSlot.rules')}>
              <div className="slot-numeric-modal-content">
                <div className="slot-numeric-modal-header">
                  <h2>
                    <FaInfoCircle className="me-2" />
                    {t('numericSlot.rules')}
                  </h2>
                  <button className="slot-numeric-modal-close" onClick={() => setShowRulesModal(false)} aria-label={t('numericSlot.close')}>
                    <FaTimesCircle />
                  </button>
                </div>
                <div className="slot-numeric-modal-body">
                  <p>
                    <FaGamepad className="me-2" />
                    {t('numericSlot.rules_intro')}
                  </p>
                  <ul className="text-start">
                    <li>
                      <FaCoins className="me-2" />
                      {t('numericSlot.rules_bet', { min: MIN_BET })}
                    </li>
                    <li>
                      {t('numericSlot.rules_triple')}
                    </li>
                    <li>
                      {t('numericSlot.rules_pair')}
                    </li>
                    <li>
                      {t('numericSlot.rules_straight')}
                    </li>
                  </ul>
                  <Button onClick={() => handleButtonClick(closeRulesModal)} className="slot-numeric-modal-button">
                    <FaCheckCircle className="me-2" /> {t('numericSlot.close')}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Modal: Historial */}
          {showHistoryModal && (
            <div className="slot-numeric-custom-modal" role="dialog" aria-modal="true" aria-label={t('numericSlot.history')}>
              <div className="slot-numeric-modal-content">
                <div className="slot-numeric-modal-header">
                  <h2>
                    <FaHistory className="me-2" />
                    {t('numericSlot.history')}
                  </h2>
                  <button className="slot-numeric-modal-close" onClick={() => setShowHistoryModal(false)} aria-label={t('numericSlot.close')}>
                    <FaTimesCircle />
                  </button>
                </div>
                <div className="slot-numeric-modal-body slot-numeric-history-modal-body">
                  {history?.length ? (
                    <ul className="list-group">
                      {history.map((entry, index) => (
                        <li key={`${entry.created_at}-${index}`} className="list-group-item">
                          <strong>{t('numericSlot.entry.result')}:</strong> {entry.result}
                          <br />
                          <strong>{t('numericSlot.entry.details')}:</strong> {entry.details}
                          <br />
                          <strong>{t('numericSlot.entry.date')}:</strong> {new Date(entry.created_at).toLocaleString()}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-muted">{t('numericSlot.no_history')}</div>
                  )}
                  <Button onClick={() => handleButtonClick(() => setShowHistoryModal(false))} className="slot-numeric-modal-button mt-3">
                    {t('numericSlot.close')}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default NumericSlotMachine;
